"use client";

import { useMemo } from "react";
import {
  ErrorNotice,
  Notice,
  StatCard,
  StatGrid,
} from "@/components/tool-kit/primitives";
import { WIDGET_H3 } from "@/components/tool-kit/styles";
import { FunctionGraph } from "@/components/tools/calculus/function-graph";
import { MathTable } from "@/components/tools/calculus/math-table";
import type {
  EULERS_METHOD_CALCULATOR_STRINGS,
  OdeView,
} from "@/components/tools/eulers-method-calculator";
import type { PlaneScales } from "@/components/tools/math/coordinate-plane";
import type { useToolNumber } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import {
  type OdeMethod,
  type OdeRow,
  referenceValues,
  STAGES,
  solveOde,
} from "@/lib/tool-kit/calculus/ode";
import { CHART_COLORS } from "@/lib/tool-kit/palette";

type Strings = typeof EULERS_METHOD_CALCULATOR_STRINGS;
type Fmt = ReturnType<typeof useToolNumber>;
type OkView = Extract<OdeView, { kind: "ok" }>;

const METHODS: readonly OdeMethod[] = ["euler", "heun", "rk4"];
const MAX_TABLE_ROWS = 24;
const HEAD_ROWS = 14;
const TAIL_ROWS = 6;
const POINT_LIMIT = 40;
const FIELD_COLUMNS = 16;
const FIELD_ROWS = 11;
const FIELD_SEGMENT = 13;

function num(fmt: Fmt, value: number): string {
  if (!Number.isFinite(value)) {
    return "—";
  }
  return value === 0 ? fmt(0) : fmt(value, { maximumSignificantDigits: 8 });
}

function err(fmt: Fmt, value: number): string {
  if (!Number.isFinite(value)) {
    return "—";
  }
  return Math.abs(value) < 1e-13
    ? fmt(0)
    : fmt(value, { maximumSignificantDigits: 3, signDisplay: "exceptZero" });
}

/** The true value at each grid point: the exact solution if given, else an RK4 reference. */
function truth(view: OkView): number[] {
  if (view.exact) {
    const { exact } = view;
    return Array.from({ length: view.n + 1 }, (_, i) =>
      exact(view.x0 + i * view.h)
    );
  }
  return referenceValues(view.f, view.x0, view.y0, view.xEnd, view.n);
}

function SlopeField({ view, scales }: { view: OkView; scales: PlaneScales }) {
  const { viewport } = scales;
  const sx = scales.x(1) - scales.x(0);
  const sy = scales.y(0) - scales.y(1);
  const segments: string[] = [];
  for (let i = 0; i < FIELD_COLUMNS; i += 1) {
    for (let j = 0; j < FIELD_ROWS; j += 1) {
      const x =
        viewport.xMin +
        ((i + 0.5) / FIELD_COLUMNS) * (viewport.xMax - viewport.xMin);
      const y =
        viewport.yMin +
        ((j + 0.5) / FIELD_ROWS) * (viewport.yMax - viewport.yMin);
      const slope = view.f(x, y);
      if (!Number.isFinite(slope)) {
        continue;
      }
      const dx = sx;
      const dy = -slope * sy;
      const length = Math.hypot(dx, dy) || 1;
      const ux = ((dx / length) * FIELD_SEGMENT) / 2;
      const uy = ((dy / length) * FIELD_SEGMENT) / 2;
      const cx = scales.x(x);
      const cy = scales.y(y);
      segments.push(
        `M${(cx - ux).toFixed(1)} ${(cy - uy).toFixed(1)} L${(cx + ux).toFixed(1)} ${(cy + uy).toFixed(1)}`
      );
    }
  }
  return (
    <path
      d={segments.join(" ")}
      fill="none"
      opacity={0.45}
      stroke={CHART_COLORS[4]}
      strokeWidth={1.2}
    />
  );
}

function Polyline({ rows, scales }: { rows: OdeRow[]; scales: PlaneScales }) {
  const path = rows
    .map(
      (row, i) =>
        `${i === 0 ? "M" : "L"}${scales.x(row.x).toFixed(1)} ${scales.y(row.y).toFixed(1)}`
    )
    .join(" ");
  return (
    <>
      <path
        d={path}
        fill="none"
        stroke={CHART_COLORS[3]}
        strokeLinejoin="round"
        strokeWidth={2.2}
      />
      {rows.length <= POINT_LIMIT
        ? rows.map((row) => (
            <circle
              cx={scales.x(row.x)}
              cy={scales.y(row.y)}
              fill={CHART_COLORS[3]}
              key={row.n}
              r={3.2}
            />
          ))
        : null}
    </>
  );
}

function tableRows(
  rows: OdeRow[],
  reference: number[],
  view: OkView,
  fmt: Fmt,
  strings: Strings
): string[][] {
  const stages = STAGES[view.method];
  const toRow = (row: OdeRow) => [
    String(row.n),
    num(fmt, row.x),
    num(fmt, row.y),
    ...Array.from({ length: stages }, (_, i) =>
      row.slopes[i] === undefined ? "" : num(fmt, row.slopes[i])
    ),
    num(fmt, reference[row.n]),
    err(fmt, row.y - reference[row.n]),
  ];
  if (rows.length <= MAX_TABLE_ROWS) {
    return rows.map(toRow);
  }
  const gap = Array.from({ length: 5 + stages }, () => strings.skipped);
  return [
    ...rows.slice(0, HEAD_ROWS).map(toRow),
    gap,
    ...rows.slice(-TAIL_ROWS).map(toRow),
  ];
}

export function OdeResults({
  view,
  strings,
  fmt,
}: {
  view: OkView;
  strings: Strings;
  fmt: Fmt;
}) {
  const { f, method, x0, y0, xEnd, n } = view;
  const run = useMemo(
    () => solveOde(f, method, x0, y0, xEnd, n),
    [f, method, x0, y0, xEnd, n]
  );
  const reference = useMemo(() => truth(view), [view]);
  const last = run.rows.at(-1);
  const complete = run.kind === "ok" && last !== undefined;
  const endTruth = reference[n];
  const comparison = METHODS.map((m) => {
    const r = solveOde(f, m, x0, y0, xEnd, n);
    const y = r.kind === "ok" ? (r.rows.at(-1)?.y ?? Number.NaN) : Number.NaN;
    return [strings.methods[m], num(fmt, y), err(fmt, y - endTruth)];
  });
  const study = [1, 2, 4, 8]
    .map((factor) => {
      const steps = n * factor;
      if (steps > 4000) {
        return null;
      }
      const r = solveOde(f, method, x0, y0, xEnd, steps);
      const y = r.kind === "ok" ? (r.rows.at(-1)?.y ?? Number.NaN) : Number.NaN;
      return [
        String(steps),
        num(fmt, Math.abs((xEnd - x0) / steps)),
        num(fmt, y),
        err(fmt, y - endTruth),
      ];
    })
    .filter((row): row is string[] => row !== null);
  const window = useMemo(() => {
    const ys = [
      ...run.rows.map((r) => r.y),
      ...(view.exact ? reference : []),
    ].filter((y) => Number.isFinite(y) && Math.abs(y) < 1e9);
    const lo = Math.min(...ys, y0);
    const hi = Math.max(...ys, y0);
    const pad = Math.max((hi - lo) * 0.12, 0.5);
    const span = Math.abs(xEnd - x0);
    const xMin = Math.min(x0, xEnd) - span * 0.04;
    const xMax = Math.max(x0, xEnd) + span * 0.04;
    return { xMin, xMax, yMin: lo - pad, yMax: hi + pad };
  }, [run, reference, view.exact, y0, x0, xEnd]);
  const methodName = strings.methods[method];
  const head = [
    strings.columnN,
    strings.columnX,
    strings.columnY,
    ...Array.from({ length: STAGES[method] }, (_, i) =>
      t(strings.columnSlope, { i: i + 1 })
    ),
    view.exact ? strings.columnExact : strings.columnReference,
    strings.columnError,
  ];
  const endLabel = num(fmt, xEnd);
  return (
    <>
      {view.requestedH !== null ? (
        <Notice>
          {t(strings.adjusted, {
            requested: num(fmt, view.requestedH),
            n,
            h: num(fmt, Math.abs(view.h)),
          })}
        </Notice>
      ) : null}
      {run.kind === "blow-up" ? (
        <ErrorNotice>{t(strings.blowUp, { at: num(fmt, run.at) })}</ErrorNotice>
      ) : null}
      <StatGrid>
        <StatCard
          emphasis
          label={t(strings.approximationLabel, { end: endLabel })}
          value={complete ? num(fmt, last.y) : strings.noExactEnd}
        />
        <StatCard
          label={t(
            view.exact
              ? strings.exactResultLabel
              : strings.referenceResultLabel,
            { end: endLabel }
          )}
          value={num(fmt, endTruth)}
        />
        <StatCard
          label={strings.errorLabel}
          value={complete ? err(fmt, last.y - endTruth) : strings.noExactEnd}
        />
        <StatCard
          label={strings.stepLabelResult}
          value={num(fmt, Math.abs(view.h))}
        />
        <StatCard label={strings.stepsLabel} value={fmt(n)} />
      </StatGrid>
      <div className="space-y-2">
        <h3 className={WIDGET_H3}>{strings.formulaHeading}</h3>
        <p className="text-foreground text-sm">{strings.formulas[method]}</p>
      </div>
      <div className="space-y-2">
        <h3 className={WIDGET_H3}>{strings.tableHeading}</h3>
        <MathTable
          head={head}
          rows={tableRows(run.rows, reference, view, fmt, strings)}
        />
        {run.rows.length > MAX_TABLE_ROWS ? (
          <p className="text-muted-foreground text-sm">
            {t(strings.tableNote, { count: run.rows.length })}
          </p>
        ) : null}
      </div>
      <div className="space-y-2">
        <h3 className={WIDGET_H3}>{strings.compareHeading}</h3>
        <MathTable
          head={[strings.columnMethod, strings.columnEnd, strings.columnError]}
          rows={comparison}
        />
      </div>
      <div className="space-y-2">
        <h3 className={WIDGET_H3}>
          {t(strings.studyHeading, { method: methodName })}
        </h3>
        <MathTable
          head={[
            strings.columnN,
            strings.columnStepSize,
            strings.columnEnd,
            strings.columnError,
          ]}
          rows={study}
        />
        <p className="text-muted-foreground text-sm">{strings.studyNote}</p>
      </div>
      <div className="space-y-3">
        <h3 className={WIDGET_H3}>{strings.graphHeading}</h3>
        <FunctionGraph
          downloadPngLabel={strings.downloadPng}
          downloadSvgLabel={strings.downloadSvg}
          filename="euler-method-graph"
          over={(scales) => <Polyline rows={run.rows} scales={scales} />}
          series={[
            ...(view.exact
              ? [
                  {
                    f: view.exact,
                    color: CHART_COLORS[2],
                    dashed: true,
                    label: strings.seriesExact,
                  },
                ]
              : []),
            {
              f: () => Number.NaN,
              color: CHART_COLORS[3],
              label: t(strings.seriesNumeric, { method: methodName }),
            },
          ]}
          title={t(strings.graphTitle, {
            method: methodName,
            x0: num(fmt, x0),
            end: endLabel,
          })}
          under={(scales) => <SlopeField scales={scales} view={view} />}
          window={window}
        />
      </div>
    </>
  );
}
